import { lazy } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider as BaseRouteProvider } from 'react-router-dom';
import { Page } from '../components/Page';
import { lazyRetry } from '../utils/staticLazyLoad';

// Shared
const AppRoot = lazy(() => lazyRetry(() => import('./App/AppRoot'), 'AppRoot'));
const NotFound = lazy(() => lazyRetry(() => import('./NotFound/NotFound'), 'NotFound'));

// Homepage
const HomepageLayout = lazy(() => lazyRetry(() => import('../layout/HomepageLayout'), 'HomepageLayout'));
const Homepage = lazy(() => lazyRetry(() => import('./Homepage/Homepage/Homepage'), 'Homepage'));

const PastTours = lazy(() => lazyRetry(() => import('./Homepage/PastTours/PastTours'), 'PastTours'));
const Signatures = lazy(() => lazyRetry(() => import('./Homepage/Signatures/SignaturesPage'), 'Signatures'));
const Vouchers = lazy(() => lazyRetry(() => import('./Homepage/Vouchers/Vouchers'), 'Vouchers'));

// Rewards
const RewardsLayout = lazy(() => lazyRetry(() => import('../layout/RewardsLayout'), 'RewardsLayout'));
const Rewards = lazy(() => lazyRetry(() => import('./Rewards/Rewards/RewardsPage'), 'Rewards'));
const Activity = lazy(() => lazyRetry(() => import('./Rewards/Activity/ActivityPage'), 'Activity'));
const EarningPoints = lazy(() => lazyRetry(() => import('./Rewards/EarningPoints/EarningPointsPage'), 'EarningPoints'));
const FAQs = lazy(() => lazyRetry(() => import('./Rewards/FAQs/FAQsPage'), 'FAQs'));
const FlightsPortal = lazy(() =>
  lazyRetry(() => import('./Rewards/FlightsPortal/FlightsPortalRedirect'), 'FlightsPortal')
);
const Unenrolled = lazy(() => lazyRetry(() => import('./Rewards/UnenrolledPage'), 'Unenrolled'));
const Restricted = lazy(() => lazyRetry(() => import('./Rewards/RestrictedPage'), 'Restricted'));

const router = createBrowserRouter(
  [
    {
      path: '/not-found',
      element: (
        <Page name='NotFoundPage'>
          <NotFound />
        </Page>
      ),
    },
    {
      path: '/',
      element: (
        <Page name='AppRoot'>
          <AppRoot>
            <Outlet />
          </AppRoot>
        </Page>
      ),
      children: [
        {
          path: '/',
          element: <Navigate to='/my-tours' replace />,
        },
        {
          path: 'my-tours',
          element: (
            <Page name='Homepage'>
              <HomepageLayout />
            </Page>
          ),
          children: [
            {
              index: true,
              element: (
                <Page name='Homepage'>
                  <Homepage />
                </Page>
              ),
            },
            {
              path: 'past',
              element: (
                <Page name='PastTours'>
                  <PastTours />
                </Page>
              ),
            },
            {
              path: 'signatures',
              element: (
                <Page name='Signatures'>
                  <Signatures />
                </Page>
              ),
            },
            {
              path: 'vouchers',
              element: (
                <Page name='Vouchers'>
                  <Vouchers />
                </Page>
              ),
            },
          ],
        },
        {
          path: 'rewards/unenrolled',
          element: (
            <Page name='Unenrolled'>
              <Unenrolled />
            </Page>
          ),
        },
        {
          path: 'rewards/restricted',
          element: (
            <Page name='Restricted'>
              <Restricted />
            </Page>
          ),
        },
        {
          path: 'rewards',
          element: (
            <Page name='Rewards'>
              <RewardsLayout />
            </Page>
          ),
          children: [
            {
              index: true,
              element: (
                <Page name='Rewards'>
                  <Rewards />
                </Page>
              ),
            },
            {
              path: 'activity',
              element: (
                <Page name='Activity'>
                  <Activity />
                </Page>
              ),
            },
            {
              path: 'earning-points',
              element: (
                <Page name='Earning points'>
                  <EarningPoints />
                </Page>
              ),
            },
            {
              path: 'faqs',
              element: (
                <Page name='FAQs'>
                  <FAQs />
                </Page>
              ),
            },
            {
              path: 'flights-portal',
              element: (
                <Page name='FlightsPortal'>
                  <FlightsPortal />
                </Page>
              ),
            },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <Navigate to='/not-found' replace={true} />,
    },
  ],
  {
    // FIXME: this needs to change to account for global rewards site not being on /my-tours
    basename: process.env.REACT_APP_ROUTER_BASENAME,
  }
);

const RouteProvider = () => <BaseRouteProvider router={router} />;

export { RouteProvider };
